import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { Navigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { errorMessages } from '~/constants/errorMessages.js';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { managerStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/styles.ts';
import {
  BLACK_LIST_CONTRAGENT_CODE_LABEL,
  BLACK_LIST_PAGE_EYEBROW,
  BLACK_LIST_START_PATH,
  DEFAULT_BLACK_LIST_ROWS_PER_PAGE,
} from '~/modules/covenants/CovenantsEmailBlackList/constants.ts';
import useSupervisorContragentBlackListActions from '~/modules/covenants/CovenantsEmailBlackList/hooks/useSupervisorContragentBlackListActions.ts';
import SupervisorContragentBlackListCard from '~/modules/covenants/CovenantsEmailBlackList/SupervisorContragentBlackListCard.tsx';
import { blackListStyles } from '~/modules/covenants/CovenantsEmailBlackList/styles.ts';
import {
  selectCurrentContragent,
  selectCurrentContragentError,
  selectCurrentContragentStatus,
  selectSupervisorContragentBlocks,
  selectSupervisorContragentBlocksError,
  selectSupervisorContragentBlocksPageConfiguration,
  selectSupervisorContragentBlocksStatus,
} from '~/store/covenants/selectors.ts';
import { resetCurrentContragent } from '~/store/covenants/slice.ts';
import {
  fetchContragentById,
  fetchSupervisorContragentBlocks,
} from '~/store/covenants/thunks/contragentThunks.ts';
import { AppDispatch } from '~/store/store.ts';

const SupervisorContragentBlackListPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_BLACK_LIST_ROWS_PER_PAGE);
  const [modalErrorText, setModalErrorText] = useState('');

  const contragent = useSelector(selectCurrentContragent);
  const contragentStatus = useSelector(selectCurrentContragentStatus);
  const contragentError = useSelector(selectCurrentContragentError);
  const blocks = useSelector(selectSupervisorContragentBlocks);
  const blocksStatus = useSelector(selectSupervisorContragentBlocksStatus);
  const blocksError = useSelector(selectSupervisorContragentBlocksError);
  const pageConfiguration = useSelector(selectSupervisorContragentBlocksPageConfiguration);

  const isContragentLoading = contragentStatus === 'loading';
  const isBlocksLoading = blocksStatus === 'loading';
  const isInitialBlocksLoading =
    isBlocksLoading && !blocks.length && pageConfiguration.totalElements === 0;

  const getErrorMessage = (
    error?: { message?: string; data?: { message?: string } } | null
  ): string => error?.message || error?.data?.message || errorMessages.SERVICE_UNAVAILABLE;
  const { isSubmittingAdd, deletingEmail, handleAddEmail, handleDeleteEmail } =
    useSupervisorContragentBlackListActions({
      dispatch,
      contragentId: contragent?.id,
      blocks,
      page,
      rowsPerPage,
      setPage,
      onError: setModalErrorText,
    });

  useEffect(() => {
    const nextError = contragentError || blocksError;

    if (nextError) {
      setModalErrorText(getErrorMessage(nextError));
    }
  }, [blocksError, contragentError]);

  useEffect(() => {
    if (!id) {
      return;
    }

    void dispatch(fetchContragentById({ id: Number(id) }));

    return () => {
      dispatch(resetCurrentContragent());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (!contragent?.id) {
      return;
    }

    void dispatch(
      fetchSupervisorContragentBlocks({
        contragentId: contragent.id,
        page,
        size: rowsPerPage,
      })
    );
  }, [contragent?.id, dispatch, page, rowsPerPage]);

  if (!id) {
    return <Navigate to={BLACK_LIST_START_PATH} replace />;
  }

  if (contragentStatus === 'success' && contragent === null) {
    return <Navigate to={BLACK_LIST_START_PATH} replace />;
  }

  if (isContragentLoading || isInitialBlocksLoading) {
    return (
      <Box sx={commonStyles.centeredLoader}>
        <CircularProgress />
      </Box>
    );
  }

  if (contragentError) {
    return <ModalAlert isOpen text={getErrorMessage(contragentError)} onClose={() => undefined} />;
  }

  if (!contragent) {
    return null;
  }

  return (
    <Box sx={commonStyles.pageRoot}>
      <Box sx={managerStyles.pageContent}>
        <Box sx={commonStyles.header}>
          <Box>
            <Typography variant="overline" sx={blackListStyles.pageEyebrow}>
              {BLACK_LIST_PAGE_EYEBROW}
            </Typography>
            <Typography variant="h4" sx={commonStyles.title}>
              {contragent.displayName}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={commonStyles.subtitle}>
              {BLACK_LIST_CONTRAGENT_CODE_LABEL} {contragent.code}
            </Typography>
          </Box>
        </Box>
        <Divider sx={managerStyles.pageDivider} />

        <Box sx={commonStyles.content}>
          <SupervisorContragentBlackListCard
            contragent={contragent}
            blocks={blocks}
            page={page}
            rowsPerPage={rowsPerPage}
            pageConfiguration={pageConfiguration}
            isLoading={isBlocksLoading}
            isSubmittingAdd={isSubmittingAdd}
            deletingEmail={deletingEmail}
            onAddEmail={handleAddEmail}
            onDeleteEmail={handleDeleteEmail}
            onPageChange={setPage}
            onRowsPerPageChange={(nextRowsPerPage) => {
              setRowsPerPage(nextRowsPerPage);
              setPage(0);
            }}
          />
        </Box>
      </Box>

      <ModalAlert
        isOpen={!!modalErrorText}
        text={modalErrorText}
        onClose={() => setModalErrorText('')}
      />
    </Box>
  );
};

export default SupervisorContragentBlackListPage;
